import { PopperProps } from "@mui/material";
import _ from "lodash";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";

import { Combobox } from "components/Feature/Combobox";
import { useAppDispatch, useAppSelector } from "hooks/useHook";
import { AUTH_MODE_TYPE } from "models/auth.model";
import { Option } from "models/common.model";
import { Fundraising } from "models/fundraising.model";
import { ProspectMultiSelectType } from "models/prospect.model";
import { selectAuthMode } from "store/auth/auth.selector";
import {
  selectFundraising,
  selectPossibleEditFundraising,
} from "store/fundraising/fundraising.selector";
import { fundraisingActions } from "store/fundraising/fundraising.slice";

interface Props {
  fieldName: string;
  name: string;
  placeholder?: string;
  freeSolo?: boolean;
  popperPlacement?: PopperProps["placement"];
  options: Option[];
}

const stringField: string[] = [];

export const FundraisingDropdown = ({
  fieldName,
  name,
  placeholder,
  freeSolo,
  options,
  popperPlacement,
}: Props) => {
  const [selectedItem, setSelectedItem] = useState<ProspectMultiSelectType>();
  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();
  const selectedFundraising = useAppSelector(selectFundraising);
  const isPossibleEditFundraising = useAppSelector(
    selectPossibleEditFundraising
  );
  const authMode = useAppSelector(selectAuthMode);
  const isAdmin = [AUTH_MODE_TYPE.ADMIN, AUTH_MODE_TYPE.ADMIN_OWNER].includes(
    authMode
  );

  const isStringField = _.includes(stringField, name);

  const fieldData = selectedFundraising[name as keyof Fundraising];

  const handleUpdateField = async (data: FieldValues) => {
    setLoading(true);

    if (isStringField) {
      if (fieldData === data?.[name]) return;
    } else {
      if ((fieldData as ProspectMultiSelectType)?.id === data?.[name]) return;
    }

    const input = isStringField
      ? data
      : {
          [name]: {
            id: data?.[name],
          },
        };

    dispatch(
      fundraisingActions.updateFundraising({
        id: selectedFundraising.id,
        input,
        callback: () => {
          setLoading(false);
        },
      })
    );
  };

  useEffect(() => {
    if ((fieldData as ProspectMultiSelectType)?.id) {
      if (isStringField) {
        setSelectedItem({
          label: fieldData as string,
          name: fieldData as string,
        } as ProspectMultiSelectType);
      } else {
        setSelectedItem({
          ...(fieldData as ProspectMultiSelectType),
          label: (fieldData as ProspectMultiSelectType).name,
        });
      }
    } else {
      setSelectedItem(undefined);
    }
  }, [fieldData, isStringField, name, options]);

  return (
    <Combobox
      fieldName={fieldName}
      id={`combobox-${name}`}
      freeSolo={freeSolo}
      options={options}
      placeholder={placeholder}
      disabled={isAdmin || !isPossibleEditFundraising}
      loading={loading}
      onchange={(value) => {
        if (!value) return;
        const data = value as ProspectMultiSelectType;

        handleUpdateField({
          [name]: isStringField ? data.name : data.id,
        });
      }}
      value={selectedItem || null}
      popperPlacement={popperPlacement}
      isOptionEqualToValue={(option, value) =>
        (option as Option).id === (value as ProspectMultiSelectType).id
      }
    />
  );
};
